import { useEffect, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Box, Link, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import request from 'api'
import { getGlobalConsentApi } from 'pages/policies/api'
import { Consent, VIEW_TYPE, buildPolicySearch } from 'pages/policies/utils'
import Detail from 'pages/policies/views/detail'
import List from 'pages/policies/views/list'

import { useDateTime, useMappedTranslation } from '../../../hooks'
import PrivacyIcon from '../assets/images/privacy_icon.png'

export const TermsConditionsList = () => {
  const { versionId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const view_type = versionId ? VIEW_TYPE.DETAIL : VIEW_TYPE.LIST
  const { state } = location
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const paramLocale = searchParams.get('locale') || undefined
  const policySearch = buildPolicySearch(searchParams)
  const theme = useTheme()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [consents, setConsents] = useState<Consent[]>()
  const [targetConsent, setTargetConsent] = useState<Consent>()
  const [detail, setDetail] = useState<string>('<div/>')
  const t = useMappedTranslation()
  const dateTime = useDateTime()
  const getConsent = () => {
    if (view_type === VIEW_TYPE.DETAIL && state?.consent !== undefined) {
      setTargetConsent(state.consent)
      return Promise.resolve([state.consent])
    }

    return getGlobalConsentApi('TC', false, {
      apiURL: '/v1/consent/registered',
      overrides: { countryCode: paramCountryCode, locale: paramLocale }
    })
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentBody')
        } else {
          const data = res.data as Consent[]
          setConsents(data)
          return data
        }
      })
      .then((data) => {
        if (view_type === VIEW_TYPE.DETAIL) {
          const target = data.find((item) => item.version === versionId)
          setTargetConsent(target)
          return [target]
        }
      })
  }

  const handleButton = (item) => {
    return navigate(`/policies/terms-conditions/${item.version}${policySearch}`, {
      state: { consent: item }
    })
  }

  useEffect(() => {
    setIsLoading(true)
    setDetail('<div/>')

    getConsent()
      .then((data) => {
        if (view_type === VIEW_TYPE.DETAIL) {
          return request.get(data[0].uri).then((res) => {
            if (res.status !== 200) {
              throw new Error('Failed getConsentBody')
            }

            const document = new DOMParser().parseFromString(res.data, 'text/html')
            const body = document.getElementsByTagName('body').item(0)?.innerHTML
            if (body !== undefined) {
              setDetail(body)
            }
          })
        }
      })
      .catch(() => {
        setConsents([])
        setTargetConsent(undefined)
        setDetail('<div>Failed to load</div>')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [versionId, paramCountryCode, paramLocale])

  return (
    <Box sx={{ width: '100%', height: '100%', padding: { xs: '24px', md: '0' }, paddingTop: { xs: '5px', md: '46px' } }}>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: { xs: '100%', md: '1040px' } }}>
          {view_type === VIEW_TYPE.LIST ? (
            <List
              title={t('policy.terms.title')}
              titleIcon={PrivacyIcon}
              consents={consents}
              isLoading={isLoading}
              handleListButton={handleButton}
              isPublishTitle
            />
          ) : (
            <Detail
              title={(targetConsent && targetConsent.title) || t('policy.terms.title')}
              isLoading={isLoading}
              detail={detail}
              action={
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    pt: { xs: '52px', md: '72px' }
                  }}
                >
                  <Typography
                    className='off'
                    fontSize='13px'
                    lineHeight='16px'
                    fontWeight='700'
                    color={theme.palette.mode === 'dark' ? '#A9A9A9' : '#636363'}
                  >
                    {t('iam.01.Common.Txt.Effective.date', {
                      0: dateTime(Number(targetConsent?.publish), { format: 'withoutTime' })
                    })}
                  </Typography>
                  <Link
                    to={`/policies/terms-conditions${policySearch}`}
                    component={RouterLink}
                    sx={{
                      mt: '12px',
                      padding: '7px 12px',
                      fontSize: '13px',
                      lineHeight: '19px',
                      minHeight: '0',
                      textTransform: 'none',
                      color: 'text.primary',
                      textDecoration: 'none',
                      border: `0.5px solid #636363`,
                      borderRadius: '24px',
                      textAlign: 'center',
                      minWidth: '152px',
                      display: 'block',
                      height: '33px'
                    }}
                    data-testid='Link6483EA25'
                  >
                    {t('iam.01.Common.Notice.detail.list.01')}
                  </Link>
                  <Link
                    component='button'
                    type='button'
                    color='text.secondary'
                    sx={{
                      mt: '24px',
                      fontSize: '16px',
                      fontHeight: '24px',
                      fontWeight: '700',
                      width: '324px'
                    }}
                    onClick={() => navigate(`/policies/terms-conditions/services${policySearch}`)}
                    data-testid='Link58E4FD8F'
                  >
                    {t('policy.service.title')}
                  </Link>
                </Box>
              }
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
